import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { DocumentDto, ErrorMessage, HttpError } from '@myarvato/shared/models';
import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { CustomizationTranslationService } from '@myarvato/shared/services';
import { DocumentsService } from '@myarvato/modules/transport/services';

export interface DetailsDocumentsState {
  documents: DocumentDto[];
  documentTypes: string[];
  isLoading: boolean;
  error: ErrorMessage | undefined;
}

export const DetailsDocumentsStore = signalStore(
  withState<DetailsDocumentsState>({
    documents: [],
    documentTypes: [],
    isLoading: false,
    error: undefined,
  }),

  withMethods((store, customizationTranslationService = inject(CustomizationTranslationService)) => ({
    _createErrorMessage: (error: unknown): ErrorMessage => {
      const httpError = error as HttpError;
      const errorCode = httpError?.error?.errorCode || 'G0400';
      return {
        severity: 'error',
        icon: 'pi pi-times-circle',
        summary: customizationTranslationService.getTranslation(`errors.${errorCode}.title`, 'errors.G0400.title'),
        detail: customizationTranslationService.getTranslation(`errors.${errorCode}.description`, 'errors.G0400.description'),
      };
    },
  })),

  withMethods((store, documentsService = inject(DocumentsService)) => ({
    loadDocuments: rxMethod<string>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap((trackingNumber: string) =>
          documentsService.getData(trackingNumber).pipe(
            tapResponse({
              next: (documents) => patchState(store, { documents: documents.documents ?? [], isLoading: false }),
              error: (err) => patchState(store, { isLoading: false, error: store._createErrorMessage(err) }),
            }),
          ),
        ),
      ),
    ),

    _loadDocumentTypes: rxMethod<void>(
      pipe(
        switchMap(() =>
          documentsService.getDocumentTypes().pipe(
            tapResponse({
              next: (types) => patchState(store, { documentTypes: types }),
              error: () => {},
            }),
          ),
        ),
      ),
    ),
  })),

  withHooks((store) => ({
    onInit: () => {
      store._loadDocumentTypes();
    },
  })),
);
